<script setup lang="ts">
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { getRedirect, redirect } from '@/lib/redirect';
import { useBillingStore } from '@/stores/billing';
import { Alert, Button, LayoutGap, Loader, Modal, Prose } from '@dabble/quillui';
import { onMounted, ref } from 'vue';

const { t } = useI18n();
const billingStore = useBillingStore();

type Status = 'loading' | 'success' | 'error';
const status = ref<Status>('loading');
const message = ref('');
const open = ref(true);

// Peek at the redirect URL up front; the existing redirect() helper will consume
// it from sessionStorage when the user clicks Continue.
const destination = getRedirect();

const code = sessionStorage.getItem('referralCode') ?? '';

onMounted(async () => {
  // Always clear the code so a leftover value can't reattach to a different
  // account later (e.g. via "Switch Account → Add another account" in the same tab).
  sessionStorage.removeItem('referralCode');

  if (!code) {
    goContinue();
    return;
  }

  await billingStore.fetchBillingOverview();

  if (!billingStore.primarySubscription) {
    status.value = 'error';
    message.value = t('referral_apply_error_no_subscription');
    return;
  }

  const ok = await billingStore.applyCouponCode(code, { silent: true });
  if (ok) {
    status.value = 'success';
    message.value = t('referral_apply_success');
  } else {
    status.value = 'error';
    message.value = billingStore.lastApplyError ?? t('billing_coupon_error_generic');
  }
});

function goContinue() {
  redirect().catch((err) => {
    logError('referral apply: redirect failed, falling back to destination', err);
    window.location.href = destination;
  });
}
</script>

<template>
  <Modal v-model="open" :title="t('referral_apply_title')" :closable="false" size="small" @close="goContinue">
    <LayoutGap direction="column" size="medium" align="center">
      <template v-if="status === 'loading'">
        <Loader />
        <Prose align="center">{{ t('referral_apply_loading') }}</Prose>
      </template>

      <template v-else>
        <Alert :variant="status === 'success' ? 'success' : 'warning'" :no-icon="false">
          {{ message }}
        </Alert>
        <Button variant="primary" @click="goContinue">{{ t('referral_apply_continue') }}</Button>
      </template>
    </LayoutGap>
  </Modal>
</template>
